"use client";

// Sign-up form (client component).
//
// Wires the `signupAction` server action to a `useActionState` hook so we get
// pending state and per-field errors back without round-tripping a JSON
// response. The form posts native `FormData` — Next.js handles streaming the
// payload to the server-side action without us writing a fetch call.
//
// Submit button uses `useFormStatus` to flip into a loading state during the
// transition. Auth-flow forms keep validation simple (zod on the server) and
// rely on `aria-invalid` + `aria-describedby` so screen readers pick up the
// per-field error text.

import * as React from "react";
import { useActionState } from "react";
import { useFormStatus } from "react-dom";
import { Button, Input, Label } from "@aio/platform-ui";
import { signupAction, type AuthActionState } from "../../../auth/actions";

const initialState: AuthActionState = {};

function SubmitButton() {
  const { pending } = useFormStatus();
  return (
    <Button type="submit" loading={pending} className="w-full">
      {pending ? "Creating account..." : "Create account"}
    </Button>
  );
}

export function SignupForm() {
  const [state, formAction] = useActionState<AuthActionState, FormData>(
    signupAction,
    initialState,
  );

  return (
    <form action={formAction} className="space-y-5" noValidate>
      {state.error && !state.fieldErrors ? (
        <div
          role="alert"
          className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger"
        >
          {state.error}
        </div>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="signup-name">Full name</Label>
        <Input
          id="signup-name"
          name="name"
          type="text"
          autoComplete="name"
          required
          aria-invalid={state.fieldErrors?.name ? true : undefined}
          aria-describedby={state.fieldErrors?.name ? "signup-name-error" : undefined}
        />
        {state.fieldErrors?.name ? (
          <p id="signup-name-error" className="text-xs text-danger">
            {state.fieldErrors.name}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-email">Email</Label>
        <Input
          id="signup-email"
          name="email"
          type="email"
          autoComplete="email"
          required
          aria-invalid={state.fieldErrors?.email ? true : undefined}
          aria-describedby={state.fieldErrors?.email ? "signup-email-error" : undefined}
        />
        {state.fieldErrors?.email ? (
          <p id="signup-email-error" className="text-xs text-danger">
            {state.fieldErrors.email}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-password">Password</Label>
        <Input
          id="signup-password"
          name="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          aria-invalid={state.fieldErrors?.password ? true : undefined}
          aria-describedby={
            state.fieldErrors?.password ? "signup-password-error" : "signup-password-hint"
          }
        />
        {state.fieldErrors?.password ? (
          <p id="signup-password-error" className="text-xs text-danger">
            {state.fieldErrors.password}
          </p>
        ) : (
          <p id="signup-password-hint" className="text-xs text-fg-muted">
            Minimum 8 characters.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-confirm">Confirm password</Label>
        <Input
          id="signup-confirm"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          required
          aria-invalid={state.fieldErrors?.confirmPassword ? true : undefined}
          aria-describedby={
            state.fieldErrors?.confirmPassword ? "signup-confirm-error" : undefined
          }
        />
        {state.fieldErrors?.confirmPassword ? (
          <p id="signup-confirm-error" className="text-xs text-danger">
            {state.fieldErrors.confirmPassword}
          </p>
        ) : null}
      </div>

      <SubmitButton />
    </form>
  );
}
